import { FC, useMemo, useRef, useState } from "react"
import { Scanner, useDevices } from "@yudiel/react-qr-scanner"
import { useTranslation } from "react-i18next"
import Callout from "components/callout/callout"
import Button from "components/inputs/button"
import SelectInput from "components/inputs/selectInput"
import { ReactComponent as CachedIcon } from "assets/icons/cached.svg"
import { runBarcodeScanHandler } from "@/utils/barcodeScanHandler"

const ScanBarcodeForm: FC = () => {
    const { t } = useTranslation()
    const devices = useDevices()
    const videoDevices = useMemo(
        () => devices.filter((device) => device.kind === "videoinput" && device.deviceId),
        [devices],
    )

    const [deviceId, setDeviceId] = useState("")
    const [paused, setPaused] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [status, setStatus] = useState<string | null>(null)
    const handlingRef = useRef(false)

    const constraints = useMemo<MediaTrackConstraints>(() => {
        if (deviceId) {
            return {
                deviceId: { exact: deviceId },
                width: { ideal: 1280 },
                height: { ideal: 720 },
            }
        }
        return {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
        }
    }, [deviceId])

    const cameraOptions = videoDevices.map((device, index) => ({
        value: device.deviceId,
        label: device.label || `${t("labelCamera")} ${index + 1}`,
    }))

    const cycleCamera = () => {
        if (videoDevices.length < 2) return
        const ids = videoDevices.map((device) => device.deviceId)
        const currentIndex = deviceId ? ids.indexOf(deviceId) : -1
        const nextIndex = currentIndex === -1 ? (ids.length > 1 ? 1 : 0) : (currentIndex + 1) % ids.length
        setDeviceId(ids[nextIndex])
        setPaused(false)
        handlingRef.current = false
        setError(null)
    }

    const handleScan = async (rawValue: string) => {
        if (!rawValue || handlingRef.current) return
        handlingRef.current = true
        setPaused(true)
        setError(null)
        setStatus(t("lookingUpBarcode", { code: rawValue }))
        try {
            const result = await runBarcodeScanHandler(rawValue)
            if (result === 'handled') return
            setStatus(null)
            setError(result === 'missing' ? t("toastBarcodeScanError") : t("toastBarcodeNotFound"))
            handlingRef.current = false
            setPaused(false)
        } catch {
            setStatus(null)
            setError(t("toastBarcodeScanError"))
            handlingRef.current = false
            setPaused(false)
        }
    }

    return (
        <div className="flex flex-col gap-y-4">
            {error && <Callout type="error">{error}</Callout>}
            {status && !error && <Callout type="info">{status}</Callout>}
            <div className="overflow-hidden rounded-md max-w-md mx-auto w-full min-h-[16rem]">
                <Scanner
                    key={deviceId || "default-camera"}
                    paused={paused}
                    constraints={constraints}
                    sound={false}
                    allowMultiple={false}
                    startTimeoutMs={8000}
                    components={{ finder: true, torch: true }}
                    styles={{
                        container: {
                            width: "100%",
                            height: "auto",
                            minHeight: "16rem",
                            aspectRatio: "1 / 1",
                        },
                    }}
                    onError={(err) => {
                        const message = typeof err?.message === "string" && err.message
                            ? err.message
                            : t("toastBarcodeScanError")
                        setError(message)
                    }}
                    onScan={(detectedCodes) => {
                        const value = detectedCodes.find((code) => code.rawValue?.trim())?.rawValue?.trim()
                        if (value) {
                            void handleScan(value)
                        }
                    }}
                />
            </div>
            {videoDevices.length > 1 && (
                <div className="flex flex-col gap-y-3">
                    <SelectInput
                        label={t("labelCamera")}
                        value={deviceId}
                        options={[
                            { value: "", label: t("labelDefaultCamera") },
                            ...cameraOptions,
                        ]}
                        onChange={(e) => {
                            setDeviceId(e.target.value)
                            setPaused(false)
                            handlingRef.current = false
                            setError(null)
                        }}
                    />
                    <Button wideOnMobile type="button" design="white" onClick={cycleCamera}>
                        <CachedIcon />
                        <span>{t("buttonSwitchCamera")}</span>
                    </Button>
                </div>
            )}
            <p className="text-sm text-gray-500">{t("scanBarcodeHint")}</p>
        </div>
    )
}

export default ScanBarcodeForm
